<template>
  <div class="main-page">
    <!-- 内容区域 - 使用router-view显示当前路由对应的页面 -->
    <div
      class="content-area"
      :class="{
        'no-bottom-tab': !showBottomTabs,
        'content-area--profile': isProfileHome,
        'content-area--flush': isNoteCardsPage,
        'content-area--summit': isSummitHome
      }"
    >
      <keep-alive>
        <router-view v-if="$route.meta && $route.meta.keepAlive" />
      </keep-alive>
      <router-view v-if="!($route.meta && $route.meta.keepAlive)" />
    </div>

    <!-- 底栏四等分 Tab -->
    <div v-if="showBottomTabs" class="bottom-tabs" :class="{ 'bottom-tabs--disabled': logoutOverlay }">
      <button
        type="button"
        class="tab-item tab-item--plain"
        :class="{ active: isActiveTab('/summary-list') }"
        :disabled="logoutOverlay"
        @click="handleTabClick('/summary-list')"
      >
        <div class="tab-icon"><i class="el-icon-s-order"></i></div>
        <div class="tab-label">内容列表</div>
      </button>
      <button
        type="button"
        class="tab-item tab-item--plain"
        aria-label="智能分析"
        :class="{ active: isActiveTab('/summit') }"
        :disabled="logoutOverlay"
        @click="handleTabClick('/summit')"
      >
        <div class="tab-icon"><i class="el-icon-s-data"></i></div>
        <div class="tab-label">智能分析</div>
      </button>
      <button
        type="button"
        class="tab-item tab-item--plain"
        :class="{ active: isActiveTab('/note-cards') }"
        :disabled="logoutOverlay"
        @click="handleTabClick('/note-cards')"
      >
        <div class="tab-icon"><i class="el-icon-edit-outline"></i></div>
        <div class="tab-label">我的笔记</div>
      </button>
      <button
        type="button"
        class="tab-item tab-item--plain"
        :class="{ active: isActiveTab('/profile') }"
        :disabled="logoutOverlay"
        @click="handleTabClick('/profile')"
      >
        <div class="tab-icon"><i class="el-icon-user-solid"></i></div>
        <div class="tab-label">我的</div>
      </button>
    </div>

    <transition name="logout-mask-fade">
      <div
        v-if="logoutOverlay"
        class="main-logout-mask"
        aria-live="polite"
        aria-busy="true"
        @click.stop
        @touchmove.prevent
      >
        <div class="main-logout-mask__panel">
          <div class="main-logout-mask__spinner" aria-hidden="true"></div>
          <p class="main-logout-mask__text">正在注销，请稍候…</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MainPage',
  data() {
    return {
      lastClickTab: null,
      lastClickTime: 0,
      doubleClickDelay: 300,
      logoutOverlay: false
    }
  },
  computed: {
    showBottomTabs() {
      const path = this.$route.path
      const hiddenPaths = ['/summit/detail', '/summary-list/detail']
      if (hiddenPaths.includes(path)) return false
      // 「我的」子页自带顶栏/返回，不占底栏预留空间
      if (path.startsWith('/profile/')) return false
      return true
    },
    isProfileHome() {
      return this.$route.path === '/profile'
    },
    isNoteCardsPage() {
      return this.$route.path === '/note-cards'
    },
    isSummitHome() {
      return this.$route.path === '/summit'
    }
  },
  mounted() {
    this.$bus.$on('logout-overlay', this.onLogoutOverlay)
  },
  beforeDestroy() {
    this.$bus.$off('logout-overlay', this.onLogoutOverlay)
  },
  methods: {
    onLogoutOverlay(visible) {
      this.logoutOverlay = !!visible
    },
    handleTabClick(path) {
      if (this.logoutOverlay) return
      const now = Date.now()
      const isDoubleClick = (
        this.lastClickTab === path && 
        (now - this.lastClickTime) < this.doubleClickDelay
      )

      if (isDoubleClick && path === '/home' && this.$route.path === '/home') {
        // 双击首页tab且当前已在首页，触发刷新并回到顶部
        this.$bus.$emit('refresh-home-page')
        this.lastClickTab = null
        this.lastClickTime = 0
        return // 不执行后续的切换逻辑
      }
      if (isDoubleClick && path === '/summary-list' && this.$route.path === '/summary-list') {
        // 双击内容列表tab，刷新列表并回到顶部
        this.$bus.$emit('refresh-summary-list')
        this.lastClickTab = null
        this.lastClickTime = 0
        return
      }
      if (isDoubleClick && path === '/note-cards' && this.$route.path === '/note-cards') {
        this.$bus.$emit('refresh-note-cards')
        this.lastClickTab = null
        this.lastClickTime = 0
        return
      }
      
      // 单击切换tab
      this.switchTab(path)
      this.lastClickTab = path
      this.lastClickTime = now
    },
    switchTab(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    isActiveTab(path) {
      if (path === '/home') {
        return this.$route.path === '/home' || this.$route.path === '/search'
      }
      if (path === '/summary-list') {
        return this.$route.path === '/summary-list'
      }
      if (path === '/note-cards') {
        return (
          this.$route.path === '/note-cards' ||
          this.$route.path === '/summary-list/notes'
        )
      }
      if (path === '/summit') {
        return (
          this.$route.path === '/summit' ||
          this.$route.path === '/summit/detail'
        )
      }
      if (path === '/profile') {
        return (
          this.$route.path === '/profile' ||
          this.$route.path.startsWith('/profile/')
        )
      }
      return this.$route.path === path
    }
  }
}
</script>

<style scoped>
.main-page {
  /* 悬浮底栏：内容区预留 = 栏高 + 底部悬浮间距（安全区在定位里另算） */
  --bottom-tab-bar-height: 68px;
  --bottom-tab-float-inset: 14px;
  --bottom-tab-side-inset: 19px;
  --bottom-tab-radius: 32px;
  --bottom-tab-height: calc(var(--bottom-tab-bar-height) + var(--bottom-tab-float-inset));
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: #f5f5f5;
  overflow: hidden;
}

.content-area {
  flex: 1;
  overflow-y: auto;
  padding-bottom: calc(var(--bottom-tab-height) + env(safe-area-inset-bottom, 0px));
  -webkit-overflow-scrolling: touch;
}

.content-area.no-bottom-tab,
.content-area.content-area--profile,
.content-area.content-area--flush,
.content-area.content-area--summit {
  padding-bottom: 0;
}

.content-area.content-area--summit {
  overflow: hidden;
  overscroll-behavior: none;
  display: flex;
  flex-direction: column;
  background: #f8f9fc;
}

.content-area.content-area--summit > * {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.bottom-tabs {
  position: fixed;
  bottom: calc(var(--bottom-tab-float-inset) + env(safe-area-inset-bottom, 0px));
  left: var(--bottom-tab-side-inset);
  right: var(--bottom-tab-side-inset);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  justify-items: center;
  min-height: var(--bottom-tab-bar-height);
  height: var(--bottom-tab-bar-height);
  padding: 5px 6px;
  gap: 2px;
  box-sizing: border-box;
  background: rgba(244, 245, 247, 0.85);
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
  border: 1px solid rgba(236, 238, 243, 0.7);
  border-radius: var(--bottom-tab-radius);
  z-index: 100;
  box-shadow:
    0 4px 16px rgba(15, 28, 68, 0.05),
    0 10px 28px rgba(15, 28, 68, 0.06);
}

.tab-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  min-width: 0;
  width: calc(100% - 8px);
  height: calc(100% - 4px);
  max-height: 58px;
  border: none;
  background: transparent;
  font: inherit;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  border-radius: var(--bottom-tab-radius);
}

.tab-item--plain:active {
  background: #f5f7fb;
}

.tab-item--plain .tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 22px;
  line-height: 1;
  margin-bottom: 2px;
  color: #8b95a8;
}

.tab-item--plain .tab-icon i {
  font-size: 22px;
  line-height: 1;
}

.tab-item--plain .tab-label {
  font-size: 10px;
  line-height: 1.2;
  color: #8b95a8;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-item--plain.active {
  /* 叠在底栏半透明底上，选中层本身要更透，否则整体发实 */
  background: rgba(170, 176, 188, 0.16);
}

.tab-item--plain.active .tab-icon,
.tab-item--plain.active .tab-label {
  color: #3546e8;
  font-weight: 600;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .main-page {
    --bottom-tab-bar-height: 64px;
    --bottom-tab-float-inset: 12px;
    --bottom-tab-side-inset: 15px;
    --bottom-tab-radius: 28px;
    --bottom-tab-height: calc(var(--bottom-tab-bar-height) + var(--bottom-tab-float-inset));
  }

  .bottom-tabs {
    padding: 4px 5px;
  }

  .tab-item {
    width: calc(100% - 6px);
    height: calc(100% - 4px);
    max-height: 54px;
    padding: 2px 2px;
  }
}

/* 滚动条样式 */
.content-area::-webkit-scrollbar {
  width: 4px;
}

.content-area::-webkit-scrollbar-track {
  background: transparent;
}

.content-area::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.content-area::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.main-logout-mask {
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(20px, env(safe-area-inset-top, 0px)) 20px max(20px, env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  background: rgba(242, 246, 255, 0.78);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  touch-action: none;
}

.main-logout-mask__panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-width: 180px;
  padding: 28px 32px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(66, 83, 255, 0.12);
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.12);
}

.main-logout-mask__spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(66, 83, 255, 0.18);
  border-top-color: #4253ff;
  animation: main-logout-spin 0.8s linear infinite;
}

.main-logout-mask__text {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #334155;
  letter-spacing: 0.04em;
}

.bottom-tabs--disabled {
  pointer-events: none;
  opacity: 0.72;
}

.logout-mask-fade-enter-active,
.logout-mask-fade-leave-active {
  transition: opacity 0.22s ease;
}

.logout-mask-fade-enter,
.logout-mask-fade-leave-to {
  opacity: 0;
}

@keyframes main-logout-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
